/**
 * GS1 Application Identifier (AI)
 *
 * 01 - GTIN: Global Trade Item Number
 * 10 - BATCH/LOT: Batch or Lot Number
 * 11 - PROD DATE: Production Date
 * 17 - DUE DATE: Expiration Date
 * 21 - SERIAL: Serial Number
 */

const GS1_GROUP_SEPARATOR = '\u001D'; // Group Separator
const GS1ApplicationIdentifiers = {
  '01': 'GTIN',
  '10': 'BATCH/LOT',
  '11': 'PROD DATE',
  '17': 'DUE DATE',
  '21': 'SERIAL',
} as const;

type GS1ApplicationIdentifiers = typeof GS1ApplicationIdentifiers;
type GS1ApplicationIdentifiersKeys = keyof GS1ApplicationIdentifiers;
type GS1ApplicationIdentifiersValues = GS1ApplicationIdentifiers[GS1ApplicationIdentifiersKeys];

const GS1DataTransformationsByAI: Partial<
  Record<GS1ApplicationIdentifiersKeys, (value: string) => string | undefined>
> = {
  '11': parseGs1Date,
  '17': parseGs1Date,
};

export function isGS1ApplicationIdentifier(ai: string): ai is GS1ApplicationIdentifiersKeys {
  return Object.keys(GS1ApplicationIdentifiers).includes(ai);
}

export function splitGS1String(
  barcode: string
): Partial<Record<GS1ApplicationIdentifiersValues, string | undefined>> {
  if (!barcode) {
    throw new Error('Invalid GS1 barcode format');
  }

  const GS = barcode.indexOf(GS1_GROUP_SEPARATOR);
  if (GS === -1) {
    throw new Error('Invalid GS1 barcode format');
  }

  const parts = barcode.split(GS1_GROUP_SEPARATOR).filter((part) => part !== '');
  const parsed: Partial<Record<GS1ApplicationIdentifiersValues, string | undefined>> = {};

  for (const part of parts) {
    const ai = part.substring(0, 2);

    if (!isGS1ApplicationIdentifier(ai)) {
      throw new Error(`Unsupported GS1 Application Identifier: ${ai}`);
    }

    const value = part.substring(2);
    const transform = GS1DataTransformationsByAI[ai];
    parsed[GS1ApplicationIdentifiers[ai]] = transform ? transform(value) : value;
  }

  return parsed;
}

function parseGs1Date(date: string | undefined | null): string | undefined {
  if (!date) {
    return;
  }

  return `20${date.substring(0, 2)}-${date.substring(2, 4)}-${date.substring(4, 6)}`;
}
